/**
 * Table — generic sortable + filterable + paginated table.
 *
 * Used in Devices list, Batches list, Audit Log, Tag explorer, etc.
 *
 * Type parameter T = the row shape. Each ColumnDef tells the table:
 *   - id              : stable key, also default sort key
 *   - header          : label for the column header
 *   - accessor        : (row) => the value used for sorting / filtering
 *   - cell            : (row) => the React node to render in the cell
 *   - sortable        : default true for accessor-returning-number-or-string
 *   - className       : extra cell class
 *   - width           : optional column width
 *
 * Filters: a single global text filter (case-insensitive substring across
 * every `accessor` value coerced to string). Per-column filters can be added
 * later if a screen needs them.
 */

import {
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { Input } from "@/lib/ui/input";
import { Button } from "@/lib/ui/button";
import { ChartSkeleton } from "../ChartSkeleton";
import { EmptyState } from "@/lib/ui/empty-state";
import type { WidgetState } from "../types";

export interface ColumnDef<T> {
  id: string;
  header: string;
  /** Cell renderer. */
  cell: (row: T) => ReactNode;
  /** Sorting/filtering accessor — defaults to identity on the cell's text. */
  accessor?: (row: T) => string | number | null | undefined;
  /** Force this column to be unsortable. */
  unsortable?: boolean;
  className?: string;
  /** Tailwind-compatible width class (e.g. "w-32"). */
  width?: string;
  /** Right-align numeric columns. */
  align?: "left" | "right" | "center";
}

export interface TableProps<T> {
  /** Row identity — used as React key + row click target. */
  getRowId: (row: T) => string;
  columns: ColumnDef<T>[];
  data: T[];
  /** Default page size. Default 25. */
  pageSize?: number;
  /** Optional row click. */
  onRowClick?: (row: T) => void;
  /** Optional search placeholder. */
  searchPlaceholder?: string;
  /** Hide search. Default false. */
  hideSearch?: boolean;
  /** Hide pagination. Default false. */
  hidePagination?: boolean;
  /** State override. */
  state?: WidgetState;
  /** Empty-state config when there are zero rows + no filter. */
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
  /**
   * Row multi-select. When set, the table renders a leading checkbox column
   * (with a header select-all/indeterminate box that toggles the currently
   * visible/filtered rows) and reports the selected ids via
   * `onSelectionChange`. Selection is controlled — the parent owns the
   * `selectedIds` set. Ticking a checkbox does NOT fire `onRowClick`.
   */
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (next: Set<string>) => void;
  /**
   * Rows that cannot be selected right now: shown greyed, their checkbox
   * disabled, and left out of select-all. Optional; every row is selectable
   * without it.
   */
  isRowDisabled?: (row: T) => boolean;
}

interface SortState {
  columnId: string;
  direction: "asc" | "desc";
}

/** Stable empty Set so the `selectedIds ?? …` default doesn't allocate a new
 *  Set each render (which would churn the selection helpers). */
const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();

export function Table<T>({
  getRowId,
  columns,
  data,
  pageSize = 25,
  onRowClick,
  searchPlaceholder = "Filter rows…",
  hideSearch = false,
  hidePagination = false,
  state = { kind: "ok" },
  emptyTitle = "No rows yet",
  emptyDescription = "Once data lands here, you'll see it.",
  className,
  selectable = false,
  selectedIds,
  onSelectionChange,
  isRowDisabled,
}: TableProps<T>) {
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(0);
  const selected = selectedIds ?? EMPTY_SELECTION;

  const filtered = useMemo(() => {
    if (!filter) return data;
    const needle = filter.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const v = col.accessor ? col.accessor(row) : null;
        if (v === null || v === undefined) return false;
        return String(v).toLowerCase().includes(needle);
      }),
    );
  }, [data, filter, columns]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.id === sort.columnId);
    if (!col || !col.accessor) return filtered;
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = col.accessor!(a);
      const bv = col.accessor!(b);
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "number" && typeof bv === "number")
        return (av - bv) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [filtered, sort, columns]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const effectivePage = Math.min(page, pageCount - 1);
  const start = effectivePage * pageSize;
  const visible = hidePagination ? sorted : sorted.slice(start, start + pageSize);

  // Selection over the FILTERED rows (so the header box selects everything the
  // current filter narrows to, not just the visible page). Controlled — we
  // build the next Set and hand it back to the parent.
  const filteredIds = (isRowDisabled ? filtered.filter((r) => !isRowDisabled(r)) : filtered).map(getRowId);
  const selectedFilteredCount = filteredIds.filter((id) => selected.has(id)).length;
  const allFilteredSelected =
    filteredIds.length > 0 && selectedFilteredCount === filteredIds.length;
  const someFilteredSelected =
    selectedFilteredCount > 0 && !allFilteredSelected;

  const toggleAllFiltered = () => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (allFilteredSelected) {
      for (const id of filteredIds) next.delete(id);
    } else {
      for (const id of filteredIds) next.add(id);
    }
    onSelectionChange(next);
  };

  const toggleRow = (id: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  if (state.kind === "loading") {
    return <ChartSkeleton shape="table" columns={columns.length} className={className} />;
  }

  if (data.length === 0 && state.kind !== "error") {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        className={className}
      />
    );
  }

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {!hideSearch && (
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-on-surface-muted" />
          <Input
            placeholder={searchPlaceholder}
            value={filter}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFilter(e.target.value);
              setPage(0);
            }}
            className="pl-8"
          />
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-surface-elevated">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-surface-muted/40">
              <tr>
                {selectable && (
                  <th scope="col" className="w-10 px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someFilteredSelected;
                      }}
                      onChange={toggleAllFiltered}
                      className="size-3.5 align-middle"
                      aria-label="Select all rows"
                    />
                  </th>
                )}
                {columns.map((col) => (
                  <th
                    key={col.id}
                    scope="col"
                    className={cn(
                      "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-on-surface-muted",
                      col.width,
                      col.align === "right" && "text-right",
                      col.align === "center" && "text-center",
                    )}
                  >
                    <HeaderCell
                      column={col}
                      sort={sort}
                      onSort={(direction) => {
                        if (!direction) {
                          setSort(null);
                        } else {
                          setSort({ columnId: col.id, direction });
                        }
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (selectable ? 1 : 0)}
                    className="px-3 py-12 text-center text-sm text-on-surface-muted"
                  >
                    No rows match "{filter}".
                  </td>
                </tr>
              ) : (
                visible.map((row) => {
                  const rowId = getRowId(row);
                  const disabled = !!isRowDisabled?.(row);
                  const isSelected = selectable && !disabled && selected.has(rowId);
                  return (
                  <tr
                    key={rowId}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      "border-b border-border last:border-b-0 transition-colors",
                      onRowClick &&
                        "cursor-pointer hover:bg-surface-muted/40",
                      isSelected && "bg-brand-muted/30",
                      disabled && "bg-surface-muted/30 text-on-surface-muted",
                    )}
                  >
                    {selectable && (
                      <td
                        className="px-3 py-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={!disabled && selected.has(rowId)}
                          disabled={disabled}
                          onChange={() => toggleRow(rowId)}
                          className="size-3.5 align-middle"
                          aria-label="Select row"
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={cn(
                          "px-3 py-2.5 text-on-surface",
                          col.align === "right" && "text-right",
                          col.align === "center" && "text-center",
                          col.className,
                        )}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!hidePagination && pageCount > 1 && (
        <div className="flex items-center justify-between gap-3 text-xs text-on-surface-muted">
          <span>
            Showing {start + 1}–{Math.min(start + pageSize, sorted.length)} of{" "}
            {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={effectivePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <span className="px-2 font-mono">
              {effectivePage + 1} / {pageCount}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={effectivePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderCell<T>({
  column,
  sort,
  onSort,
}: {
  column: ColumnDef<T>;
  sort: SortState | null;
  onSort: (direction: "asc" | "desc" | null) => void;
}) {
  const sortable = column.accessor && !column.unsortable;
  if (!sortable) {
    return <span>{column.header}</span>;
  }
  const active = sort?.columnId === column.id;
  const Icon = !active
    ? ChevronsUpDown
    : sort?.direction === "asc"
      ? ChevronUp
      : ChevronDown;

  return (
    <button
      type="button"
      onClick={() => {
        if (!active) onSort("asc");
        else if (sort?.direction === "asc") onSort("desc");
        else onSort(null);
      }}
      className={cn(
        "inline-flex items-center gap-1 transition-colors",
        active ? "text-on-surface" : "hover:text-on-surface",
      )}
    >
      {column.header}
      <Icon className="size-3" />
    </button>
  );
}
